import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Grid,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Modal,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    getFirestore,
    collection,
    getDocs,
    doc,
    getDoc,
    writeBatch,
    query,
    where,
    deleteDoc,
    updateDoc,
    orderBy
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import Takkevariheader from './Takkevariheader';

const db = getFirestore();

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', md: 450 },
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: '8px',
};

function Intresttaken() {
    const { loanid } = useParams();

    const [loanData, setLoanData] = useState(null);
    const [customerData, setCustomerData] = useState({});
    const [interestHistory, setInterestHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
    const [isPaid, setIsPaid] = useState(true);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // principal modal
    const [openPrincipalModal, setOpenPrincipalModal] = useState(false);
    const [principalAmount, setPrincipalAmount] = useState("");
    const [principalDate, setPrincipalDate] = useState(
        new Date().toISOString().slice(0, 10)
    );
    const [isProcessingPrincipal, setIsProcessingPrincipal] = useState(false);

    // 🔹 Fetch Loan + History
    const fetchLoanAndHistory = useCallback(async () => {
        setLoading(true);
        try {
            const loanSnap = await getDoc(doc(db, 'takkevariloans', loanid));
            if (!loanSnap.exists()) {
                toast.error("Loan not found!");
                setLoading(false);
                return;
            }
            const loan = loanSnap.data();
            setLoanData({ id: loanSnap.id, ...loan });

            // Customer fetch
            const customerSnap = await getDoc(doc(db, 'takkevaricusts', loan.customerId));
            if (customerSnap.exists()) {
                setCustomerData(customerSnap.data());
            }

            // Interest history
            const q = query(
                collection(db, 'takkevariintrests'),
                where('takkevariloansId', '==', loanid),
                orderBy('interestTakenDate', 'desc') // 🔹 sort by date desc
            );
            const snapshot = await getDocs(q);
            const history = snapshot.docs.map(d => ({
                id: d.id,
                interestAmount: d.data().interestAmount,
                interestTakenDate: d.data().interestTakenDate || null,
                status: d.data().status || "जमा नाही"
            }));
            setInterestHistory(history);
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch loan or customer data.");
        } finally {
            setLoading(false);
        }
    }, [loanid]);

    useEffect(() => {
        fetchLoanAndHistory();
    }, [fetchLoanAndHistory]);

    // 🔹 Open Modal
    // 🔹 Open Modal
    const handleOpenModal = (record = null) => {
        if (!loanData) return;

        if (record) {
            // Edit case
            setEditingId(record.id);
            setPaymentAmount(record.interestAmount ? Number(record.interestAmount).toFixed(2) : "0.00");
            setPaymentDate(
                record.interestTakenDate
                    ? moment(record.interestTakenDate.toDate ? record.interestTakenDate.toDate() : record.interestTakenDate).format('YYYY-MM-DD')
                    : new Date().toISOString().slice(0, 10)
            );
            setIsPaid(record.status === "Paid");
        } else {
            // New record case → check if last unpaid exists
            let monthlyInterest = loanData.monthlyInterestAmount
                ? Number(loanData.monthlyInterestAmount)
                : 0;

            // find unpaid total
            const unpaidSum = interestHistory
                .filter(h => h.status !== "Paid")
                .reduce((acc, h) => acc + Number(h.interestAmount), 0);

            const defaultAmount = unpaidSum > 0 ? unpaidSum + monthlyInterest : monthlyInterest;

            setEditingId(null);
            setPaymentAmount(defaultAmount.toFixed(2));
            setPaymentDate(new Date().toISOString().slice(0, 10));
            setIsPaid(true);
        }
        setOpenModal(true);
    };


    // 🔹 Close Modal
    const handleCloseModal = () => {
        setOpenModal(false);
        setPaymentAmount('');
        setEditingId(null);
    };

    // 🔹 Confirm Payment
    const handleConfirmPayment = async () => {
        if (!paymentAmount || isNaN(paymentAmount)) {
            toast.error("Enter valid amount");
            return;
        }

        setIsProcessingPayment(true);
        try {
            if (editingId) {
                await updateDoc(doc(db, 'takkevariintrests', editingId), {
                    interestAmount: parseFloat(paymentAmount),
                    interestTakenDate: new Date(paymentDate),
                    status: isPaid ? "Paid" : "Unpaid",
                });
                toast.success("व्याज रेकॉर्ड अद्यतन यशस्वी!");
            } else {
                const batch = writeBatch(db);
                const interestRef = doc(collection(db, 'takkevariintrests'));
                batch.set(interestRef, {
                    interestAmount: parseFloat(paymentAmount),
                    interestTakenDate: new Date(paymentDate),
                    status: isPaid ? "paid" : "Unpaid",
                    takkevariloansId: loanid,
                });
                await batch.commit();
                toast.success("व्याज जमा यशस्वी!");
            }
            handleCloseModal();
            fetchLoanAndHistory();
        } catch (error) {
            console.error(error);
            toast.error("Failed to save payment.");
        } finally {
            setIsProcessingPayment(false);
        }
    };

    // 🔹 Delete record
    const handleDelete = async (id) => {
        if (!window.confirm("तुम्हाला हा रेकॉर्ड हटवायचा आहे का?")) return;
        try {
            await deleteDoc(doc(db, 'takkevariintrests', id));
            toast.success("रेकॉर्ड हटवला गेला.");
            fetchLoanAndHistory();
        } catch (error) {
            console.error(error);
            toast.error("रेकॉर्ड हटवण्यात अयशस्वी.");
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <CircularProgress />
            </Box>
        );
    }


    // open/close principal modal
    const handleOpenPrincipalModal = () => setOpenPrincipalModal(true);
    const handleClosePrincipalModal = () => {
        setOpenPrincipalModal(false);
        setPrincipalAmount("");
    };

    // confirm principal
    const handleConfirmPrincipal = async () => {
        if (!principalAmount || isNaN(principalAmount)) {
            toast.error("कृपया योग्य रक्कम भरा");
            return;
        }
        setIsProcessingPrincipal(true);
        try {
            const loanRef = doc(db, "takkevariloans", loanid);
            const loanSnap = await getDoc(loanRef);
            if (!loanSnap.exists()) {
                toast.error("Loan not found!");
                return;
            }
            const loan = loanSnap.data();
            const updatedJamaRakkam =
                (loan.jamaRakkam || 0) + parseFloat(principalAmount);
            const updatedAmount = (loan.amount || 0) - parseFloat(principalAmount);

            await updateDoc(loanRef, {
                jamaRakkam: updatedJamaRakkam,
                jamaDinank: new Date(principalDate),
                amount: updatedAmount,
            });

            toast.success("मुद्दल यशस्वीपणे जमा केले!");
            handleClosePrincipalModal();
            fetchLoanAndHistory();
        } catch (error) {
            console.error(error);
            toast.error("मुद्दल जमा करण्यात अयशस्वी");
        } finally {
            setIsProcessingPrincipal(false);
        }
    };

    return (
        <Box>
            <Takkevariheader />

            {/* Loan & Customer Info */}
            {loanData && (
                <Card sx={{
                    mb: 3,
                    borderRadius: '16px',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    p: 2,
                    bgcolor: '#f9fafb',
                    position: 'relative',
                }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>कर्ज सारांश</Typography>

                        <Grid container className="row">
                            <Grid className="col-sm-3 mb-3">
                                <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                    <Typography variant="subtitle2" color="text.secondary">घेतलेली रक्कम</Typography>
                                    <Typography variant="h6">{loanData.ghetleliRakkam}</Typography>
                                </Box>
                            </Grid>

                            <Grid className="col-sm-3 mb-3">
                                <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                    <Typography variant="subtitle2" color="text.secondary">खातेदाराचे नाव</Typography>
                                    <Typography variant="h6">{customerData.name}</Typography>
                                </Box>
                            </Grid>

                            <Grid className="col-sm-3 mb-3">
                                <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                    <Typography variant="subtitle2" color="text.secondary">दिलेलेली रक्कम</Typography>
                                    <Typography variant="h6">₹{Number(loanData.amount).toFixed(2)}</Typography>
                                </Box>
                            </Grid>

                            <Grid className="col-sm-3 mb-3">
                                <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                    <Typography variant="subtitle2" color="text.secondary">दिनांक</Typography>
                                    <Typography variant="h6">{loanData.loandate ? moment(loanData.loandate.toDate ? loanData.loandate.toDate() : loanData.loandate).format('DD-MM-YYYY') : '-'}</Typography>
                                </Box>
                            </Grid>

                            <Grid className="col-sm-3 mb-3">
                                <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                    <Typography variant="subtitle2" color="text.secondary">व्याज दर</Typography>
                                    <Typography variant="h6">{loanData.interestRate}%</Typography>
                                </Box>
                            </Grid>

                            <Grid className="col-sm-3 mb-3">
                                <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                    <Typography variant="subtitle2" color="text.secondary">मासिक व्याज</Typography>
                                    <Typography variant="h6">₹{Number(loanData.monthlyInterestAmount).toFixed(2)}</Typography>
                                </Box>
                            </Grid>

                            <Grid className="col-sm-3 mb-3">
                                <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                    <Typography variant="subtitle2" color="text.secondary">जमा रक्कम</Typography>
                                    <Typography variant="h6">{loanData.jamaRakkam}</Typography>
                                </Box>
                            </Grid>

                            <Grid className="col-sm-3 mb-3">
                                <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                    <Typography variant="subtitle2" color="text.secondary"> जमा दिनांक</Typography>
                                    <Typography variant="h6">{loanData.jamaDinank ? moment(loanData.jamaDinank.toDate ? loanData.jamaDinank.toDate() : loanData.jamaDinank).format('DD-MM-YYYY') : '-'}</Typography>
                                </Box>
                            </Grid>

                            <Grid className="col-sm-3 mb-3">
                                <Button variant="contained" color="primary" onClick={() => handleOpenModal(null)}>
                                    व्याज जमा करा
                                </Button>&nbsp;
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleOpenPrincipalModal}
                                >
                                    मुद्दल जमा करा
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Interest History Table */}
            <Paper sx={{ p: 2, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>व्याज रेकॉर्ड्स</Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell>क्र. नं.</TableCell>
                                <TableCell>रक्कम (₹)</TableCell>
                                <TableCell>दिनांक</TableCell>
                                <TableCell>स्थिती</TableCell>
                                <TableCell>क्रिया</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {interestHistory.length > 0 ? interestHistory.map((row, index) => (
                                <TableRow key={row.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{Number(row.interestAmount).toFixed(2)}</TableCell>
                                    <TableCell>{row.interestTakenDate ? moment(row.interestTakenDate.toDate ? row.interestTakenDate.toDate() : row.interestTakenDate).format('DD-MM-YYYY') : '-'}</TableCell>
                                    <TableCell>{row.status}</TableCell>
                                    <TableCell>
                                        <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => handleOpenModal(row)}>Edit</Button>
                                        <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(row.id)}>Delete</Button>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">कोणतेही रेकॉर्ड्स आढळले नाहीत.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Payment Modal */}
            <Modal open={openModal} onClose={handleCloseModal}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" mb={3} sx={{ fontWeight: 600 }}>व्याज पेमेंट नोंदवा</Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="number"
                                label="रक्कम (₹)"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(e.target.value)}
                                helperText={`मासिक व्याज: ₹${loanData?.monthlyInterestAmount ? Number(loanData.monthlyInterestAmount).toFixed(2) : "0.00"}`}
                                sx={{ mb: 1 }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="date"
                                label="दिनांक"
                                value={paymentDate}
                                onChange={(e) => setPaymentDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ mb: 1 }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <InputLabel id="status-label">स्थिती</InputLabel>
                                <Select
                                    labelId="status-label"
                                    value={isPaid ? "Paid" : "Unpaid"}
                                    label="स्थिती"
                                    onChange={(e) => setIsPaid(e.target.value === "Paid")}
                                >
                                    <MenuItem value="Paid">Paid</MenuItem>
                                    <MenuItem value="Unpaid">Unpaid</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                        <Button variant="outlined" color="error" onClick={handleCloseModal}>रद्द करा</Button>
                        <Button variant="contained" onClick={handleConfirmPayment} disabled={isProcessingPayment}>
                            {isProcessingPayment ? <CircularProgress size={24} /> : 'पेमेंट कन्फर्म करा'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Principal Modal */}
            <Modal open={openPrincipalModal} onClose={handleClosePrincipalModal}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" mb={3} sx={{ fontWeight: 600 }}>
                        मुद्दल जमा करा
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="number"
                                label="रक्कम (₹)"
                                value={principalAmount}
                                onChange={(e) => setPrincipalAmount(e.target.value)}
                                sx={{ mb: 1 }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="date"
                                label="दिनांक"
                                value={principalDate}
                                onChange={(e) => setPrincipalDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ mb: 1 }}
                            />
                        </Grid>
                    </Grid>

                    <Box
                        sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}
                    >
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleClosePrincipalModal}
                        >
                            रद्द करा
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirmPrincipal}
                            disabled={isProcessingPrincipal}
                        >
                            {isProcessingPrincipal ? (
                                <CircularProgress size={24} />
                            ) : (
                                "मुद्दल कन्फर्म करा"
                            )}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}

export default Intresttaken;
